import type { PaginatedResponse } from "@/types";

const ACCESS_TOKEN_KEY = "wcloud_access_token";
const REFRESH_TOKEN_KEY = "wcloud_refresh_token";
// Parked admin tokens while an impersonation session is active — presence of
// this key is what "isImpersonating" means.
const IMPERSONATOR_ACCESS_KEY = "wcloud_impersonator_access_token";
const IMPERSONATOR_REFRESH_KEY = "wcloud_impersonator_refresh_token";

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATOR_ACCESS_KEY);
  localStorage.removeItem(IMPERSONATOR_REFRESH_KEY);
}

export function isImpersonating(): boolean {
  return localStorage.getItem(IMPERSONATOR_ACCESS_KEY) !== null;
}

/** Parks the current (admin) tokens aside and switches active auth to the
 * impersonation access token. Impersonation sessions get no refresh token,
 * so they naturally expire with the short-lived access token. */
export function startImpersonatorSession(impersonationAccessToken: string) {
  const adminAccess = getAccessToken();
  const adminRefresh = getRefreshToken();
  if (adminAccess) localStorage.setItem(IMPERSONATOR_ACCESS_KEY, adminAccess);
  if (adminRefresh) localStorage.setItem(IMPERSONATOR_REFRESH_KEY, adminRefresh);
  localStorage.setItem(ACCESS_TOKEN_KEY, impersonationAccessToken);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/** Restores the parked admin tokens. Returns false (no-op) if not impersonating. */
export function endImpersonatorSession(): boolean {
  const adminAccess = localStorage.getItem(IMPERSONATOR_ACCESS_KEY);
  if (!adminAccess) return false;
  const adminRefresh = localStorage.getItem(IMPERSONATOR_REFRESH_KEY);
  localStorage.setItem(ACCESS_TOKEN_KEY, adminAccess);
  if (adminRefresh) localStorage.setItem(REFRESH_TOKEN_KEY, adminRefresh);
  else localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATOR_ACCESS_KEY);
  localStorage.removeItem(IMPERSONATOR_REFRESH_KEY);
  return true;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  const res = await fetch("/api/v1.0/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) {
    clearTokens();
    return false;
  }
  const json = await res.json();
  setTokens(json.data.access_token, json.data.refresh_token);
  return true;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  isForm?: boolean;
  /** Return the parsed JSON body as-is instead of unwrapping its `.data` field. */
  raw?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const { body, isForm, headers, raw, ...rest } = options;
  const accessToken = getAccessToken();

  const finalHeaders: Record<string, string> = { ...(headers as Record<string, string>) };
  if (!isForm) finalHeaders["Content-Type"] = "application/json";
  if (accessToken) finalHeaders["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(path, {
    ...rest,
    headers: finalHeaders,
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
  });

  if (res.status === 401 && !retried && getRefreshToken()) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    const refreshed = await refreshPromise;
    if (refreshed) {
      return request<T>(path, options, true);
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get("content-type") || "";
  const json = contentType.includes("application/json") ? await res.json() : null;

  if (!res.ok) {
    throw new ApiError(res.status, json?.error || res.statusText);
  }

  return (raw ? json : (json?.data ?? json)) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "DELETE" }),
  postForm: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form, isForm: true }),
  /** For paginated list endpoints, which respond with {data, meta} — a plain
   * `get` would unwrap `.data` and silently drop `meta` (and break any code
   * that expects the envelope). */
  getPaginated: <T>(path: string, options?: RequestOptions) =>
    request<PaginatedResponse<T>>(path, { ...options, method: "GET", raw: true }),
};
